import { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import { Card, CardContent } from '@/shared/ui/card';
import {
  TranslationItem,
  TranslationLanguageCode,
  TRANSLATION_LANGUAGES,
} from './types';
import { cn } from '@/shared/lib/utils';

export interface TranslationsFieldProps {
  /** Lista de traduções (controlado) */
  value: TranslationItem[];
  /** Callback quando a lista muda. Ao remover, envia a lista sem o item removido. */
  onChange: (translations: TranslationItem[]) => void;
  /** Placeholder do textarea */
  placeholder?: string;
  /** Label do campo */
  label?: string;
  /** Descrição/ajuda */
  description?: string;
  /** Erro de validação */
  error?: string;
  /** Desabilitado */
  disabled?: boolean;
  /** Classes adicionais */
  className?: string;
  /** Altura mínima do textarea */
  minHeight?: string;
}

export function TranslationsField({
  value,
  onChange,
  placeholder = 'Digite a descrição...',
  label = 'Descrição',
  description,
  error,
  disabled = false,
  className,
  minHeight = '100px',
}: TranslationsFieldProps) {
  const [localTranslations, setLocalTranslations] = useState<TranslationItem[]>(() =>
    value.length > 0 ? value : [{ languageCode: 'PT_BR', description: '' }]
  );

  useEffect(() => {
    const normalized = value.length > 0 ? value : [{ languageCode: 'PT_BR' as const, description: '' }];
    setLocalTranslations(normalized);
    if (value.length === 0) {
      onChange(normalized);
    }
  }, [value]);

  const availableToAdd = TRANSLATION_LANGUAGES.filter(
    (lang) => !localTranslations.some((t) => t.languageCode === lang.code)
  );

  const getLanguageLabel = (code: TranslationLanguageCode) =>
    TRANSLATION_LANGUAGES.find((l) => l.code === code)?.label ?? code;

  const handleDescriptionChange = (languageCode: TranslationLanguageCode, description: string) => {
    const next = localTranslations.map((t) =>
      t.languageCode === languageCode ? { ...t, description } : t
    );
    setLocalTranslations(next);
    onChange(next);
  };


  const handleAdd = (code: TranslationLanguageCode) => {
    const next = [...localTranslations, { languageCode: code, description: '' }];
    setLocalTranslations(next);
    onChange(next);
  };

  const handleRemove = (languageCode: TranslationLanguageCode) => {
    const next = localTranslations.filter((t) => t.languageCode !== languageCode);
    setLocalTranslations(next);
    onChange(next);
  };

  return (
    <div className={cn('space-y-2', className)}>
      <Label className="text-white">{label}</Label>
      {description && (
        <p className="text-sm text-muted-foreground">{description}</p>
      )}

      <div className="space-y-3">
        {localTranslations.map((item) => (
          <Card
            key={item.languageCode}
            className="bg-white/[0.03] border-white/[0.06]"
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <Label className="text-xs text-white/70 mb-2 block">
                    {getLanguageLabel(item.languageCode)}
                  </Label>
                  <Textarea
                    value={item.description}
                    onChange={(e) =>
                      handleDescriptionChange(item.languageCode, e.target.value)
                    }
                    placeholder={placeholder}
                    disabled={disabled}
                    className={cn(
                      'bg-white/[0.03] border-white/[0.06] text-white',
                      error && 'border-red-500'
                    )}
                    style={{ minHeight }}
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemove(item.languageCode)}
                  disabled={disabled || localTranslations.length <= 1}
                  className="shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                  aria-label={`Remover tradução em ${getLanguageLabel(item.languageCode)}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}

        {availableToAdd.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {availableToAdd.map((lang) => (
              <Button
                key={lang.code}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleAdd(lang.code)}
                disabled={disabled}
                className="border-dashed border-white/20 text-white/80 hover:bg-white/5"
              >
                <Plus className="h-4 w-4 mr-2" />
                Adicionar em {lang.label}
              </Button>
            ))}
          </div>
        )}
      </div>

      {error && (
        <p className="text-red-500 text-sm">{error}</p>
      )}
    </div>
  );
}
